import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnInit, ViewEncapsulation } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription, pipe } from 'rxjs';
import { CONFIG_TOKEN } from '@seco/core';
import { LoginConfig, LoginConfigService, LoginAlertModel } from '@seco/login';
import { PageScrollService } from 'ngx-page-scroll-core';
import { LoginService } from './login.service';
import {
  getDirectLogin,
  getError,
  getLoginLoadingState,
  selectClpConfig,
  selectLoginUiState
} from './login-index.reducer';
import { LanguageService } from './language.service';
import { DirectLoginState, LoginUiState } from './core.reducer';
import { CookieService } from 'ngx-cookie-service';
import { GetLocaleListError, GetMenuListError } from './core.actions';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import { LoginStatus } from './../login/login.status';
import { LocaleMapperService } from './../helpers/locale-mapper.service';
import { EnvironmentConfig } from '../models';
import { ConfigService } from '../helpers/config.service';
import { map, take, filter } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'login-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LayoutComponent implements OnInit, AfterViewChecked {
  loggedInErrorModel$: Observable<LoginAlertModel[]>;

  loginFormState: Observable<LoginUiState>;
  loginFormStep = 'form';

  loggedInErrorMsg: String;

  loginLoading$: Observable<any>;
  loginLoading = false;
  listOfLang$: Observable<any>;
  isAppLoadedForSSO: boolean;
  selectedLng = 'English';
  selectedLngLocale = 'en_US';
  selectedLngStoreCode = '';
  languages = [];
  menu;
  menuHtml;
  subscriptionsArray: Subscription[] = [];
  directLoggedIn$: Observable<DirectLoginState>;
  nonceErrorCode = 201;
  public unsignedAgreement = false;
  public loadRedirectionComponent = false;
  public menuExists = false;
  private _needsDomPatch = false;
  public languageCode = 'en';
  private urlToRedirect: string;
  private javaApiUrl: string;
  readonly landingWeHaveMovedAndChangedName = $localize`:@@layout-component.landing-we-have-moved-and-changed-name:We have moved and changed name`;
  readonly landingRedirectionMessageOne = $localize`:@@layout-component.landing-redirection-message-one:Everything you could do in Amadeus Direct is still available, but now from <i>Amadeus Service Hub</i>, under a new section called <i>Agency</i>.`;
  readonly landingRedirectionMessageTwo = $localize`:@@layout-component.landing-redirection-message-two:Add the new page to your bookmarks or favorites, so you can easily find it. The Amadeus Direct landing page will no longer be available.`;
  readonly landingGoToASH = $localize`:@@layout-component.landing-go-to-ash:Go to Amadeus Service Hub`;
  readonly landingNewDesignSignIn = $localize`:@@layout-component.landing-newDesignSignIn:Sign in`;
  readonly landingNewDesignUsingYour = $localize`:@@layout-component.landingNewDesignUsingYour:Using your`;
  readonly landingNewDesignAmadeusLinks = $localize`:@@layout-component.landingNewDesignAmadeusLinks:Amadeus Links`;
  readonly landingNewDesignAmadeusCredentials = $localize`:@@layout-component.landingNewDesignAmadeusCredentials:Amadeus credentials`;
  readonly landingLoginTooltip = $localize`:@@layout-component.landingLoginTooltip:
  If you are a Selling Platform Connect user, please use the same credentials. If you are not a Selling Platform Connect user,
  please use the credentials you should have received in the email called Amadeus Security Service.
  If not received, please contact your Amadeus Service Desk`;
  readonly loginForgetPasswordLabel = $localize`:@@layout-component.loginForgetPasswordLabel:Forgot your password?`;
  readonly mobileNumberEnterLabel = $localize`:@@layout-component.mobileNumberEnterLabel:Enter your mobile number to continue,
  a verification code will be sent to your phone.`;
  readonly verificationCodeEnterLabel = $localize`:@@layout-component.verificationCodeEnterLabel:
  Enter the verification code sent to phone number.<br>Didn't receive the verification code? please contact your administrator`;
  readonly otpEnterLabel = $localize`:@@layout-component.otpEnterLabel:
  For your security, our sign-in process requires additional verification.
  Please enter the One Time Password that was sent for the user`;
  readonly loginForgetPasswordDetailLabel = $localize`:@@layout-component.loginForgetPasswordDetailLabel:
  Please enter your user details and your new password.`;
  readonly amadeusServiceHubTagline = $localize`:@@layout-component.service-hub-tagline:Helping Amadeus Customers Succeed`;
  readonly safeDomains = ['uat.servicehub.amadeus.com', 'servicehub.amadeus.com', 'integration.servicehub.amadeus.com'];
  readonly validPaths = ['/home?login_panel=travel&default_option=userid'];
  readonly httpsUrl = 'https://';
  constructor(
    private readonly loginService: LoginService,
    private el: ElementRef,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    private pageScrollService: PageScrollService,
    @Inject(DOCUMENT) private document: any,
    @Inject(CONFIG_TOKEN) private config: LoginConfig,
    private store: Store<any>,
    private languageService: LanguageService,
    private readonly loginConfigService: LoginConfigService,
    private cookieService: CookieService,
    private readonly localeMapperService: LocaleMapperService,
    private readonly initService: ConfigService,
    @Inject('ASH_URL') private readonly ASH_URL: string,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.loginService.getClpConfig();
    this.directLoggedIn$ = this.store.select(getDirectLogin);
    this.loginLoading$ = this.store.pipe(select(getLoginLoadingState));
  }

  ngOnInit() {
    this.setMeta();
    this.clearpreferredStoreLocal();
     this.loadRedirectionComponent =this.initService.getEnvConfig().AD_IS_PROD;
    const selectedLocale = localStorage.getItem('landingLang');
    this.isAppLoadedForSSO = localStorage.getItem('source')?.toLocaleLowerCase() === 'ash' || localStorage.getItem('source')?.toLocaleLowerCase() === 'snow';

    this.loginLoading$.subscribe(isLoading => {
      this.loginLoading = isLoading;
    });

    combineLatest([this.directLoggedIn$, this.initService.getEnvConfigObs()]).subscribe(
      (data: [DirectLoginState, EnvironmentConfig]) => {
        if (data[1] && data[0]?.loginStatus === LoginStatus.NONE) {
          this.loginService
            .getLocaleList('all')
            .pipe(
              take(1),
              map(languages => this.languagelabelMap(languages))
            )
            .subscribe(
              locales => {
                this.languages = locales;
                this.selectLanguage(this.languages, selectedLocale);
              },
              () => {
                this.store.dispatch(new GetLocaleListError());
              }
            );
        }
      }
    );
    this.directLoggedIn$.subscribe(loginState => {
      if (loginState.loginStatus === 'ERROR') {
        let errorMessage = $localize`:@@layout-component.landingAuthorizationFail:
        Oops - it appears something went wrong with your credentials, please try again.
        If you wish to join Amadeus Direct, you may contact your Amadeus commercial representative.
        Error code: ${loginState.code.toString()}`;
        if (loginState.code === this.nonceErrorCode) {
          errorMessage = $localize`:@@layout-component.landingAuthorizationFailReload:Oops...Something went wrong. Please refresh the page and try again.
          ${loginState.code.toString()}`;
        }
        this.loggedInErrorMsg = errorMessage;
        this.pageScrollService.scroll({ document: this.document, scrollTarget: '.creds' });
      }
    });

    // Subscribe once here – NOT inside ngAfterViewChecked – to avoid creating
    // new subscriptions on every CD cycle, which causes
    // ExpressionChangedAfterItHasBeenCheckedError in Angular 21 and leaves the
    // login form invisible until a user event re-triggers change detection.
    this.loggedInErrorModel$ = this.store.pipe(select(getError));
    this.loggedInErrorModel$.subscribe(alerts => {
      if (alerts && alerts[0] !== undefined && alerts[0].error.message !== undefined) {
        this.loggedInErrorMsg = alerts[0].error.message;
        this.pageScrollService.scroll({ document: this.document, scrollTarget: '.creds' });
      } else {
        this.loggedInErrorMsg = undefined;
      }
    });

    this.loginFormState = this.store.pipe(select(selectLoginUiState));
    this.loginFormState.subscribe(step => {
      this.loginFormStep = LoginUiState[step];
      this._needsDomPatch = true;
    });

    // adding this for fixing login page right click issue, change detection done manually to render the page.
    this.loginConfigService.getConfig()
      .pipe(filter(config => !!config), take(1))
      .subscribe(() => {
        setTimeout(() => this.ngZone.run(() => this.cdr.detectChanges()), 0);
      });
  }

  ngAfterViewChecked() {
    if (!this._needsDomPatch) {
      return;
    }

    this._needsDomPatch = false;
    switch (this.loginFormStep) {
      case 'form':
        this.patchLoginForm();
        break;
      case 'forgetPwd':
        this.patchForgetPasswordForm();
        break;
    }
  }

  private patchLoginForm(): void {
    const formSelector = '#loginForm > ama-ng-login-login-view > form';
    const checkboxSelector = `${formSelector} > div.form-check.form-check-inline.mb-3`;
    const forgotButton = this.queryLoginElement(`${formSelector} > div:nth-child(4) > button`);
    const checkboxContainer = this.queryLoginElement(checkboxSelector);
    const checkboxButton = this.queryLoginElement(`${checkboxSelector} > div`);
    const loginButton = this.queryLoginElement(`${formSelector} > div:nth-child(6) > button`);

    forgotButton?.classList.add('login-form-forgot-btn-rewrite');
    checkboxContainer?.classList.add('checkbox-container-no-padding-left');
    checkboxContainer?.classList.remove('form-check');
    checkboxButton?.classList.remove('form-check');
    checkboxButton?.classList.add('custom-control', 'custom-checkbox', 'checkbox-container-no-padding-left');
    loginButton?.classList.add('login-form-login-btn-rewrite');
    loginButton?.classList.toggle('disabled', this.loginLoading);
  }

  private patchForgetPasswordForm(): void {
    const formSelector = '#loginForm > ama-ng-login-forget-pwd > form > div:nth-child(5)';
    const backButton = this.queryLoginElement(`${formSelector} > button.btn.btn-outline-primary.me-2`);
    const restoreButton = this.queryLoginElement(`${formSelector} > button.btn.btn-primary`);

    backButton?.classList.add('login-form-forget-pwd-back');
    restoreButton?.classList.add('login-form-forget-pwd-restore');
  }

  private queryLoginElement(selector: string): HTMLElement | null {
    return this.el.nativeElement.querySelector(selector);
  }

  scrollTo(id) {
    this.pageScrollService.scroll({ document: this.document, scrollTarget: '.' + id });
  }

  // Called on change of language
  changeClpLng(language) {
    if (!this.languageService.supportedLocales[language.locale]) {
      this.config.language = this.languageService.defaultLocale;
    } else {
      this.config.language = language.locale;
    }
    this.languageService.setLanguage(language.locale);
    this.localeMapperService.updateLandingAndCookie(language.locale, language.store);
    this.refreshLoginPage();
  }

  // Call to get the footer links in Amadeus Direct
  amdeusDirectLink(language) {
    this.loginService.getMenuList(language.store).subscribe(
      menu => {
        this.menu = menu;
        this.menuHtml = this.menuHtml = this.buildAmadeusLink();

        this.unsignedAgreement =
          this.cookieService.get('unsignedAgreement') !== undefined &&
          this.cookieService.get('unsignedAgreement') === 'true';

        if (this.unsignedAgreement) {
          this.loggedInErrorMsg = $localize`:@@layout-component.userRoleChanged:Your user role has been changed. Please log in again to continue working on Amadeus Direct portal.`;
        }
        this.cookieService.delete('unsignedAgreement');
      },
      () => {
        this.store.dispatch(new GetMenuListError());
      }
    );
  }

  // Build Amadeus links
  buildAmadeusLink() {
    let result;
    result = `<div class="row links-row">`;
    if (this.menu && this.menu.landing.length) {
      this.menu.landing.forEach((amadeusLink, cnt) => {
        const titileDescriptionArray = amadeusLink.title.split('||');
        const title = titileDescriptionArray[0] ? titileDescriptionArray[0] : '';
        const description = titileDescriptionArray[1] ? titileDescriptionArray[1] : '';

        if (title.includes('Amadeus Direct') && !this.loadRedirectionComponent) {
          return;
        }
        result += `
                    <div class="link-wrap col-xl-3 col-md-6 col-12">
                        <a target="_blank" href="${amadeusLink.external_link}" class="amadeus-link">
                            <span class="link-title">${title}</span>
                            <span class="link-description">${description}</span>
                        </a>
                    </div>`;
      });
    }


    return result;
  }

  setMeta() {
    this.metaService.addTag(
      {
        name: 'description',
        content: `The exclusive community portal for SME travel agencies to manage their accounts, monitor their business, stay informed and earn rewards! All in a professional 24/7, B2C-like platform. Find out from your local representative when Amadeus direct is coming to you.`
      },
      true
    );
  }

  //**clearpreferredStoreLocal */
  clearpreferredStoreLocal() {
    if (localStorage.getItem('preferredStoreLocal')) {
      localStorage.removeItem('preferredStoreLocal');
      localStorage.removeItem('preferredLocaleLocal');
      localStorage.removeItem('source');
    }
  }

  // update the language related parameter
  updateLanguageParam(lang) {
    this.selectedLng = lang.title;
    this.selectedLngLocale = lang.locale;
    this.selectedLngStoreCode = lang.store;
    this.languageService.setLanguage(lang.locale);
    this.localeMapperService.updateLandingAndCookie(lang.locale, lang.store);
  }

  /**
   * Selecting Application Language
   * @param languages
   * @param selectedLocale
   */
  selectLanguage(languages, selectedLocale: String) {
    let language;
    languages.find(lang => {
      if (selectedLocale && selectedLocale === lang.locale) {
        language = lang;
      }
      if (!selectedLocale && lang.is_default) {
        language = lang;
      }
    });
    this.updateLanguageParam(language);
  }

  /**
   * Refresh the login page.
   */
  refreshLoginPage() {
    this.document.location.reload();
  }
  /**
   * changing title from the list which we are getting
   */
  languagelabelMap(languages) {
    return languages.map(language => ({
      ...language,
      title: this.languageService.supportedLocales[language.locale]?.title || language.title
    }));
  }

  redirectToAsh() {
    this.languageCode = localStorage.landingLang;
    this.languageCode = this.languageCode.substring(0, 2);
    this.languageCode = this.languageCode === 'id' ? 'in' : this.languageCode;
    this.languageCode = this.languageCode === 'he' ? 'iw' : this.languageCode;

    this.javaApiUrl = this.initService.getEnvConfig()?.AD_JAVA_API_URL;

    if (this.javaApiUrl?.includes('uat') && this.javaApiUrl?.includes('ws')) {
      this.urlToRedirect = this.httpsUrl + this.safeDomains[0];
    } else if (this.javaApiUrl?.includes('ws')) {
      this.urlToRedirect = this.httpsUrl + this.safeDomains[1];
    } else {
      this.urlToRedirect = this.httpsUrl + this.safeDomains[2];
    }

    const redirectionLink= this.urlToRedirect + '/' + this.languageCode + this.validPaths[0];

    if (this.checkIfSafeDomain(redirectionLink)) {
      const encodedUrl = encodeURI(redirectionLink);
      location.href = encodedUrl;
    } else {
      location.href = '';
    }
  }

  checkIfSafeDomain(url: string): boolean {
    const parsed= new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    const isSafeDomain= this.safeDomains.some(domain => 
      hostname === domain.toLowerCase()
    );

    const normalizedPath= decodeURIComponent(parsed.pathname);

    const segments = normalizedPath.split('/').filter(segment => segment !== '');
    const resolvedSegments: string[] = [];

    for (const segment of segments) {
      if (segment === '..') {
        resolvedSegments.pop();
      } else if (segment !== '.') {
        resolvedSegments.push(segment);
      }
    }

    const pathAfterLanguage = '/' + resolvedSegments[resolvedSegments.length-1];
    const pathWithQuery = pathAfterLanguage + parsed.search;

    const isValidPath = this.validPaths.includes(pathWithQuery);
  
    return isSafeDomain && isValidPath;
  }
}
